import React, { useEffect, useMemo, useState } from 'react'
import { useCatalog } from '../../hooks/useCatalog'
import CatalogNav from '../../components/catalog/CatalogNav'
import MatrixCell from '../../components/catalog/MatrixCell'

const DEFAULT_X = 'tool'
const DEFAULT_Y = 'agency'

function DimensionSelect({ label, value, onChange, dimensions, excludeSlug }) {
  return (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
      >
        {dimensions.map((dim) => (
          <option key={dim.id} value={dim.slug} disabled={dim.slug === excludeSlug}>
            {dim.name}
          </option>
        ))}
      </select>
    </label>
  )
}

function SkeletonGrid() {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-5">
      <div className="h-64 animate-pulse bg-slate-50 rounded"></div>
    </div>
  )
}

export default function MatrixView() {
  const { fetchDimensions, fetchMatrixView } = useCatalog()

  const [dimensions, setDimensions] = useState([])
  const [xSlug, setXSlug] = useState(DEFAULT_X)
  const [ySlug, setYSlug] = useState(DEFAULT_Y)

  const [matrix, setMatrix] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Load dimensions once on mount.
  useEffect(() => {
    let cancelled = false
    fetchDimensions()
      .then((data) => {
        if (cancelled) return
        const list = data || []
        setDimensions(list)

        // If the default axes don't exist (fresh install, renamed slugs),
        // fall back to the first two dimensions.
        const slugs = list.map((d) => d.slug)
        if (!slugs.includes(xSlug) || !slugs.includes(ySlug)) {
          setXSlug(list[0]?.slug || DEFAULT_X)
          setYSlug(list[1]?.slug || list[0]?.slug || DEFAULT_Y)
        }
      })
      .catch((err) => !cancelled && setError(err.message))
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Refetch matrix whenever either axis changes. When both axes are the same
  // dimension (single-dimension installs, or the user picked the same one
  // twice) there's nothing meaningful to cross — surface that instead of
  // rendering a silent blank.
  const sameAxis = Boolean(xSlug && ySlug && xSlug === ySlug)

  useEffect(() => {
    if (!xSlug || !ySlug || xSlug === ySlug) {
      setMatrix(null)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    fetchMatrixView(xSlug, ySlug)
      .then((data) => { if (!cancelled) setMatrix(data) })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load matrix')
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [xSlug, ySlug, fetchMatrixView])

  // Index the sparse cell list so renders are O(1) lookups.
  const cellMap = useMemo(() => {
    if (!matrix?.cells) return new Map()
    const map = new Map()
    matrix.cells.forEach((cell) => {
      map.set(`${cell.x}::${cell.y}`, cell)
    })
    return map
  }, [matrix])

  const maxCount = useMemo(() => {
    if (!matrix?.cells?.length) return 0
    return matrix.cells.reduce((max, cell) => Math.max(max, cell.count || 0), 0)
  }, [matrix])

  const renderCell = (xValue, yValue) => {
    const cell = cellMap.get(`${xValue.slug}::${yValue.slug}`)
    return (
      <MatrixCell
        key={`${xValue.slug}::${yValue.slug}`}
        count={cell?.count || 0}
        xDimensionSlug={matrix.dimension_x.slug}
        yDimensionSlug={matrix.dimension_y.slug}
        xValueSlug={xValue.slug}
        yValueSlug={yValue.slug}
        previewEntries={(cell?.preview_ids || []).map((id) => `Entry #${id}`)}
        maxCount={maxCount}
      />
    )
  }

  const swapAxes = () => {
    setXSlug(ySlug)
    setYSlug(xSlug)
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Matrix</h2>
        <p className="mt-1 text-sm text-slate-500">
          Cross-tabulate any two dimensions to see where the work overlaps.
        </p>
      </div>

      <CatalogNav />

      {/* Axis selectors */}
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white p-4">
        <DimensionSelect
          label="X axis"
          value={xSlug}
          onChange={setXSlug}
          dimensions={dimensions}
          excludeSlug={ySlug}
        />
        <DimensionSelect
          label="Y axis"
          value={ySlug}
          onChange={setYSlug}
          dimensions={dimensions}
          excludeSlug={xSlug}
        />
        <button
          type="button"
          onClick={swapAxes}
          className="ml-auto rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          ⇆ Swap axes
        </button>
      </div>

      {loading && <SkeletonGrid />}

      {sameAxis && !loading && !error && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          Pick two different dimensions to cross-tabulate — the axes are
          currently the same.
        </div>
      )}

      {error && !loading && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {!loading && !error && matrix && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 overflow-x-auto">
          {matrix.values_x.length === 0 || matrix.values_y.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">
              One or both dimensions have no values yet — nothing to cross-tabulate.
            </p>
          ) : (
            <table className="border-separate border-spacing-1">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-white"></th>
                  {matrix.values_x.map((xValue) => (
                    <th
                      key={xValue.id}
                      className="h-24 px-2 text-xs font-medium text-slate-500 align-bottom"
                    >
                      <div className="origin-bottom-left -rotate-45 whitespace-nowrap translate-x-1">
                        {xValue.name}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.values_y.map((yValue) => (
                  <tr key={yValue.id}>
                    <th
                      scope="row"
                      className="sticky left-0 bg-white pr-3 text-right text-xs font-medium text-slate-700 whitespace-nowrap"
                    >
                      {yValue.name}
                    </th>
                    {matrix.values_x.map((xValue) => (
                      <td key={`${yValue.id}-${xValue.id}`} className="p-0">
                        {renderCell(xValue, yValue)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && !error && matrix && matrix.cells.length > 0 && (
        <p className="text-xs text-slate-500">
          Click a cell to open the filtered catalog view for that combination.
        </p>
      )}
    </div>
  )
}
